import { useEffect, useState } from "react";
import { Plus, X } from "lucide-react";
import { api, PLACEHOLDER } from "@/lib/api";
import { useSite } from "@/lib/store";
import { Switch } from "@/components/ui/switch";
import { ProductPicker } from "@/admin/ProductPicker";
import { Btn, Field, Loading, PageHead, Panel, RowTools, SaveBtn, inputCls, move, useSiteDraft } from "@/admin/ui";

function Pinned({ ids, onChange, testid }) {
  const [cache, setCache] = useState({});
  const key = ids.join(",");
  useEffect(() => {
    const missing = ids.filter((id) => !cache[id]);
    if (missing.length) api.post("/products/lookup", { ids: missing }).then((r) => setCache((c) => ({ ...c, ...Object.fromEntries(r.data.map((p) => [p.id, p])) })));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  return (
    <div>
      <ProductPicker exclude={ids} onPick={(p) => { setCache({ ...cache, [p.id]: p }); onChange([...ids, p.id]); }} testid={`${testid}-picker`} />
      {ids.length > 0 && (
        <ul className="mt-3 space-y-1">
          {ids.map((id, i) => {
            const p = cache[id];
            return (
              <li key={id} className="flex items-center gap-2 text-sm bg-zinc-50 border border-zinc-200 px-2 py-1">
                <img src={p?.image || PLACEHOLDER} alt="" className="w-7 h-7 object-cover" />
                <span className="flex-1 truncate">{p ? `${p.artist} – ${p.title}` : "…"}</span>
                <RowTools i={i} len={ids.length} onMove={(d) => onChange(move(ids, i, d))} testid={`${testid}-pin-${i}`} />
                <button onClick={() => onChange(ids.filter((x) => x !== id))} className="p-1 text-zinc-400 hover:text-red"><X className="w-4 h-4" /></button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export default function HomeSections() {
  const { site } = useSite();
  const { draft, patch, save, saving } = useSiteDraft(["home_sections"]);
  if (!draft) return <Loading />;
  const secs = draft.home_sections;
  const set = (v) => patch("home_sections", v);
  const upd = (i, k, v) => set(secs.map((s, j) => (j === i ? { ...s, [k]: v } : s)));
  const genres = (site?.menu || []).filter((m) => m.type === "genre").map((m) => m.value);
  return (
    <div className="max-w-4xl">
      <PageHead title="Home page sections" desc="Each section shows up to 12 records. Pick records by hand, or leave the list empty to show the newest from the chosen source.">
        <SaveBtn save={save} saving={saving} />
      </PageHead>
      <div className="space-y-4">
        {secs.map((s, i) => (
          <Panel key={s.id || i} className={s.hidden ? "opacity-60" : ""} data-testid={`home-section-row-${i}`}>
            <div className="flex flex-wrap gap-4 items-end">
              <div className="w-40"><Field label="Small red label"><input value={s.label} onChange={(e) => upd(i, "label", e.target.value)} className={inputCls} data-testid={`home-section-label-${i}`} /></Field></div>
              <div className="flex-1 min-w-[200px]"><Field label="Heading"><input value={s.title} onChange={(e) => upd(i, "title", e.target.value)} className={inputCls} data-testid={`home-section-title-${i}`} /></Field></div>
              <div className="w-40"><Field label="Source">
                <select value={s.type} onChange={(e) => upd(i, "type", e.target.value)} className={inputCls} data-testid={`home-section-type-${i}`}>
                  <option value="new">New arrivals</option><option value="sale">On sale</option><option value="genre">Genre</option><option value="custom">Hand-picked only</option>
                </select></Field></div>
              {s.type === "genre" && <div className="w-40"><Field label="Genre">
                <select value={s.genre} onChange={(e) => upd(i, "genre", e.target.value)} className={inputCls} data-testid={`home-section-genre-${i}`}>
                  <option value="">Choose…</option>{genres.map((g) => <option key={g} value={g}>{g}</option>)}
                </select></Field></div>}
              <label className="flex items-center gap-2 text-sm h-10"><Switch checked={!s.hidden} onCheckedChange={(v) => upd(i, "hidden", !v)} data-testid={`home-section-visible-${i}`} /> Visible</label>
              <RowTools i={i} len={secs.length} onMove={(d) => set(move(secs, i, d))} onDelete={() => set(secs.filter((_, j) => j !== i))} testid={`home-section-${i}`} />
            </div>
            <div className="mt-4"><Field label="Hand-picked records (optional)"><Pinned ids={s.product_ids || []} onChange={(v) => upd(i, "product_ids", v)} testid={`home-section-${i}`} /></Field></div>
          </Panel>
        ))}
        <Btn variant="outline" onClick={() => set([...secs, { label: "Genre", title: "New section", type: "genre", genre: "", product_ids: [], hidden: false }])} data-testid="home-section-add"><Plus className="w-4 h-4" /> Add section</Btn>
      </div>
    </div>
  );
}
